import { z } from "zod";
import { Rng } from "../rng.js";
import type { GameModule, GeneratedVariant, Hint, SettingsField, ValidationResult } from "../types.js";

// Пазл микросхем: слоистая плата, сверху источники питания, снизу лампочки,
// между ними гнёзда. Логику задаёт микросхема, а не плата:
//   линейное гнездо (1 вход)  — повторитель или инвертор;
//   гнездо вентиля (3 входа)  — элемент И или элемент ИЛИ.
// Набор микросхем ровно равен числу гнёзд, и плата попадает в игру только если
// ровно одна раскладка этого набора зажигает все лампочки.

export const CHIP_TYPES = ["rep", "inv", "and", "or"] as const;
export type ChipType = (typeof CHIP_TYPES)[number];

export type NodeKind = "source" | "linear" | "gate" | "lamp";

export interface BoardNode {
  id: string;
  kind: NodeKind;
  col: number;
  row: number;
  inputs: string[];
}

export interface BoardLink {
  from: string;
  to: string;
}

export const circuitPuzzleSettingsSchema = z.object({
  cols: z.number().int().min(3).max(8).default(5),
  rows: z.number().int().min(6).max(14).default(10),
  layerRows: z.array(z.number().int()).length(5).default([0, 2, 4, 6, 9]),
  sourcesMin: z.number().int().min(1).max(5).default(2),
  sourcesMax: z.number().int().min(1).max(5).default(3),
  lampsMin: z.number().int().min(1).max(4).default(1),
  lampsMax: z.number().int().min(1).max(4).default(2),
  minSockets: z.number().int().min(3).max(14).default(7),
  maxFanout: z.number().int().min(2).max(5).default(3),
  requireUnique: z.boolean().default(true),
  minArrangements: z.number().int().min(1).default(12),
  chipTypes: z.array(z.enum(CHIP_TYPES)).min(2).default([...CHIP_TYPES]),
  poolSize: z.number().int().min(5).max(500).default(50),
});

export type CircuitPuzzleSettings = z.infer<typeof circuitPuzzleSettingsSchema>;

const nodeSchema = z.object({
  id: z.string(),
  kind: z.enum(["source", "linear", "gate", "lamp"]),
  col: z.number().int(),
  row: z.number().int(),
  inputs: z.array(z.string()),
});

export const circuitPuzzlePayloadSchema = z.object({
  cols: z.number().int(),
  rows: z.number().int(),
  nodes: z.array(nodeSchema),
  links: z.array(z.object({ from: z.string(), to: z.string() })),
  chips: z.record(z.enum(CHIP_TYPES), z.number().int()),
});

export type CircuitPuzzlePayload = z.infer<typeof circuitPuzzlePayloadSchema>;

export const circuitPuzzleSolutionSchema = z.object({
  placement: z.record(z.string(), z.enum(CHIP_TYPES)),
});

export type CircuitPuzzleSolution = z.infer<typeof circuitPuzzleSolutionSchema>;

const FIELDS: SettingsField[] = [
  { path: "cols", label: "Столбцов", kind: "number", min: 3, max: 8, group: "Плата" },
  { path: "rows", label: "Строк", kind: "number", min: 6, max: 14, group: "Плата" },
  { path: "sourcesMin", label: "Источников от", kind: "number", min: 1, max: 5, group: "Плата" },
  { path: "sourcesMax", label: "Источников до", kind: "number", min: 1, max: 5, group: "Плата" },
  { path: "lampsMin", label: "Лампочек от", kind: "number", min: 1, max: 4, group: "Плата" },
  { path: "lampsMax", label: "Лампочек до", kind: "number", min: 1, max: 4, group: "Плата" },
  { path: "minSockets", label: "Минимум гнёзд", kind: "number", min: 3, max: 14, group: "Сложность" },
  { path: "maxFanout", label: "Дорожек из узла", kind: "number", min: 2, max: 5, group: "Плата" },
  { path: "requireUnique", label: "Только единственное решение", kind: "boolean", group: "Сложность" },
  { path: "minArrangements", label: "Минимум раскладок набора", kind: "number", min: 1, group: "Сложность" },
  {
    path: "chipTypes",
    label: "Микросхемы в наборе",
    kind: "multiselect",
    options: [
      { value: "rep", label: "Повторитель" },
      { value: "inv", label: "Инвертор" },
      { value: "and", label: "И" },
      { value: "or", label: "ИЛИ" },
    ],
    group: "Плата",
  },
  { path: "poolSize", label: "Размер пула плат", kind: "number", min: 5, max: 500, group: "Сложность" },
];

export const SOCKET_OF: Record<ChipType, NodeKind> = {
  rep: "linear",
  inv: "linear",
  and: "gate",
  or: "gate",
};

/** Значения на всех узлах для конкретной раскладки микросхем */
export function evaluateBoard(nodes: BoardNode[], placement: Record<string, ChipType>): Record<string, boolean> {
  const value: Record<string, boolean> = {};
  for (const node of nodes) {
    if (node.kind === "source") {
      value[node.id] = true;
    } else if (node.kind === "lamp") {
      value[node.id] = Boolean(value[node.inputs[0]!]);
    } else {
      const chip = placement[node.id];
      if (!chip) {
        value[node.id] = false;
      } else if (node.kind === "linear") {
        const signal = Boolean(value[node.inputs[0]!]);
        value[node.id] = chip === "rep" ? signal : !signal;
      } else {
        const signals = node.inputs.map((id) => Boolean(value[id]));
        value[node.id] = chip === "and" ? signals.every(Boolean) : signals.some(Boolean);
      }
    }
  }
  return value;
}

const combinations = (n: number, k: number): number => {
  if (k < 0 || k > n) return 0;
  let result = 1;
  for (let i = 1; i <= k; i++) result = (result * (n - k + i)) / i;
  return Math.round(result);
};

function layerPlan(settings: CircuitPuzzleSettings, rng: Rng): NodeKind[][] {
  const cols = settings.cols;
  const sources = rng.int(settings.sourcesMin, settings.sourcesMax);
  const linear1 = rng.pick([3, 3, 4]);

  const gates2 = rng.pick([1, 1, 2]);
  let linear2 = rng.pick([1, 2]);
  while (gates2 + linear2 > cols) linear2--;

  const gates3 = gates2 + linear2 >= 3 && rng.next() < 0.6 ? 1 : 0;
  let linear3 = rng.pick([1, 2]);
  while (gates3 + linear3 > cols) linear3--;

  const lamps = rng.int(settings.lampsMin, settings.lampsMax);

  const layer2 = rng.shuffle([
    ...Array<NodeKind>(gates2).fill("gate"),
    ...Array<NodeKind>(Math.max(linear2, 0)).fill("linear"),
  ]);
  const layer3 = rng.shuffle([
    ...Array<NodeKind>(gates3).fill("gate"),
    ...Array<NodeKind>(Math.max(linear3, 0)).fill("linear"),
  ]);

  return [
    Array<NodeKind>(sources).fill("source"),
    Array<NodeKind>(linear1).fill("linear"),
    layer2,
    layer3,
    Array<NodeKind>(lamps).fill("lamp"),
  ];
}

function buildLayout(
  settings: CircuitPuzzleSettings,
  rng: Rng,
): { nodes: BoardNode[]; links: BoardLink[] } | null {
  const plan = layerPlan(settings, rng);
  const nodes: BoardNode[] = [];
  const layers: BoardNode[][] = [];
  const counters: Record<NodeKind, number> = { source: 0, linear: 0, gate: 0, lamp: 0 };
  const prefix: Record<NodeKind, string> = { source: "s", linear: "u", gate: "g", lamp: "p" };
  const columns = Array.from({ length: settings.cols }, (_, i) => i);

  for (let depth = 0; depth < plan.length; depth++) {
    const kinds = plan[depth]!;
    if (!kinds.length || kinds.length > settings.cols) return null;
    const cols = rng.sample(columns, kinds.length).sort((a, b) => a - b);
    const layer: BoardNode[] = [];
    kinds.forEach((kind, i) => {
      const node: BoardNode = {
        id: `${prefix[kind]}${counters[kind]++}`,
        kind,
        col: cols[i]!,
        row: settings.layerRows[depth]!,
        inputs: [],
      };
      nodes.push(node);
      layer.push(node);
    });
    layers.push(layer);
  }

  const links: BoardLink[] = [];
  const fanout = new Map<string, number>(nodes.map((n) => [n.id, 0]));

  for (let depth = 1; depth < layers.length; depth++) {
    const parents = layers[depth - 1]!;
    for (const node of layers[depth]!) {
      const need = node.kind === "gate" ? 3 : 1;
      const free = parents.filter((p) => (fanout.get(p.id) ?? 0) < settings.maxFanout);
      if (free.length < need) return null;
      // ближние по столбцу дорожки короче и реже пересекаются
      const sorted = [...free].sort(
        (a, b) => Math.abs(a.col - node.col) - Math.abs(b.col - node.col) || rng.next() - 0.5,
      );
      const chosen = need > 1 ? sorted.slice(0, need) : [rng.pick(sorted.slice(0, 2))];
      for (const parent of chosen) {
        fanout.set(parent.id, (fanout.get(parent.id) ?? 0) + 1);
        node.inputs.push(parent.id);
        links.push({ from: parent.id, to: node.id });
      }
    }
    // узел без единого потребителя обрывает схему
    if (parents.some((p) => (fanout.get(p.id) ?? 0) === 0)) return null;
  }

  return { nodes, links };
}

interface SolveResult {
  placement: Record<string, ChipType>;
  chips: Record<ChipType, number>;
  arrangements: number;
}

/** Ищет набор микросхем, который раскладывается по гнёздам ровно одним верным способом */
function solveBoard(nodes: BoardNode[], settings: CircuitPuzzleSettings): SolveResult | null {
  const linear = nodes.filter((n) => n.kind === "linear").map((n) => n.id);
  const gates = nodes.filter((n) => n.kind === "gate").map((n) => n.id);
  const lamps = nodes.filter((n) => n.kind === "lamp").map((n) => n.id);
  if (!lamps.length) return null;

  const linearOptions = settings.chipTypes.filter((c) => SOCKET_OF[c] === "linear");
  const gateOptions = settings.chipTypes.filter((c) => SOCKET_OF[c] === "gate");
  if ((linear.length && linearOptions.length < 1) || (gates.length && gateOptions.length < 1)) return null;

  const found = new Map<string, Record<string, ChipType>[]>();

  const linearVariants = cartesian(linearOptions, linear.length);
  const gateVariants = cartesian(gateOptions, gates.length);

  for (const lin of linearVariants) {
    for (const gate of gateVariants) {
      const placement: Record<string, ChipType> = {};
      linear.forEach((id, i) => {
        placement[id] = lin[i]!;
      });
      gates.forEach((id, i) => {
        placement[id] = gate[i]!;
      });
      const value = evaluateBoard(nodes, placement);
      if (!lamps.every((id) => value[id])) continue;
      const key = countsKey(placement);
      const list = found.get(key) ?? [];
      list.push(placement);
      found.set(key, list);
    }
  }

  for (const [, variants] of found) {
    if (settings.requireUnique && variants.length !== 1) continue;
    const placement = variants[0]!;
    const chips = countChips(placement);
    // в наборе должны быть все выбранные виды микросхем
    if (settings.chipTypes.some((type) => (chips[type] ?? 0) < 1)) continue;

    const reps = chips.rep ?? 0;
    const ands = chips.and ?? 0;
    const arrangements = combinations(linear.length, reps) * combinations(gates.length, ands);
    if (arrangements < settings.minArrangements) continue;

    return { placement, chips, arrangements };
  }
  return null;
}

function cartesian(options: ChipType[], length: number): ChipType[][] {
  if (length === 0) return [[]];
  if (!options.length) return [];
  let result: ChipType[][] = [[]];
  for (let i = 0; i < length; i++) {
    const next: ChipType[][] = [];
    for (const prefix of result) {
      for (const option of options) next.push([...prefix, option]);
    }
    result = next;
  }
  return result;
}

function countChips(placement: Record<string, ChipType>): Record<ChipType, number> {
  const chips: Record<ChipType, number> = { rep: 0, inv: 0, and: 0, or: 0 };
  for (const type of Object.values(placement)) chips[type]++;
  return chips;
}

const countsKey = (placement: Record<string, ChipType>): string => {
  const chips = countChips(placement);
  return CHIP_TYPES.map((t) => `${t}:${chips[t]}`).join("|");
};

function generate(
  settings: CircuitPuzzleSettings,
  seed: number,
): GeneratedVariant<CircuitPuzzlePayload, CircuitPuzzleSolution> {
  const rng = new Rng(seed);

  for (let attempt = 0; attempt < 4000; attempt++) {
    const layout = buildLayout(settings, rng);
    if (!layout) continue;
    const sockets = layout.nodes.filter((n) => n.kind === "linear" || n.kind === "gate");
    if (sockets.length < settings.minSockets) continue;

    const solved = solveBoard(layout.nodes, settings);
    if (!solved) continue;

    return {
      payload: {
        cols: settings.cols,
        rows: settings.rows,
        nodes: layout.nodes,
        links: layout.links,
        chips: solved.chips,
      },
      solution: { placement: solved.placement },
      metrics: {
        sockets: sockets.length,
        arrangements: solved.arrangements,
        lamps: layout.nodes.filter((n) => n.kind === "lamp").length,
      },
    };
  }

  throw new Error("circuit_puzzle: не удалось собрать плату с такими настройками");
}

function validate(
  payload: CircuitPuzzlePayload,
  submitted: CircuitPuzzleSolution,
  _settings: CircuitPuzzleSettings,
): ValidationResult {
  const placement = submitted.placement ?? {};
  const sockets = payload.nodes.filter((n) => n.kind === "linear" || n.kind === "gate");

  for (const socket of sockets) {
    const chip = placement[socket.id];
    if (!chip) return { solved: false, reason: "socket_empty", mistakes: 0 };
    if (SOCKET_OF[chip] !== socket.kind) return { solved: false, reason: "wrong_socket", mistakes: 1 };
  }
  if (Object.keys(placement).length !== sockets.length) {
    return { solved: false, reason: "extra_chips", mistakes: 1 };
  }

  const used = countChips(placement);
  for (const type of CHIP_TYPES) {
    if ((used[type] ?? 0) !== (payload.chips[type] ?? 0)) {
      return { solved: false, reason: "chip_set_mismatch", mistakes: 1 };
    }
  }

  const value = evaluateBoard(payload.nodes, placement);
  const lamps = payload.nodes.filter((n) => n.kind === "lamp");
  const dark = lamps.filter((n) => !value[n.id]).length;

  return {
    solved: dark === 0,
    reason: dark ? "lamps_dark" : undefined,
    mistakes: dark,
    details: { lamps: lamps.length, dark },
  };
}

function minDurationMs(payload: CircuitPuzzlePayload): number {
  const sockets = payload.nodes.filter((n) => n.kind === "linear" || n.kind === "gate").length;
  return Math.max(4000, sockets * 700);
}

function hint(payload: CircuitPuzzlePayload, solution: CircuitPuzzleSolution, used: number): Hint | null {
  const sockets = payload.nodes.filter((n) => n.kind === "linear" || n.kind === "gate");
  const socket = sockets[used % sockets.length];
  if (!socket) return null;
  const chip = solution.placement[socket.id];
  if (!chip) return null;
  const names: Record<ChipType, string> = {
    rep: "повторитель",
    inv: "инвертор",
    and: "элемент И",
    or: "элемент ИЛИ",
  };
  return {
    kind: "socket_chip",
    data: { socket: socket.id, chip },
    text: `В подсвеченное гнездо идёт ${names[chip]}`,
  };
}

export const circuitPuzzle: GameModule<CircuitPuzzleSettings, CircuitPuzzlePayload, CircuitPuzzleSolution> = {
  code: "circuit_puzzle",
  title: "Пазл микросхем",
  description: "Разложить микросхемы так, чтобы питание дошло до всех лампочек",
  settingsSchema: circuitPuzzleSettingsSchema,
  payloadSchema: circuitPuzzlePayloadSchema,
  solutionSchema: circuitPuzzleSolutionSchema,
  defaultSettings: circuitPuzzleSettingsSchema.parse({}),
  settingsFields: FIELDS,
  generate,
  validate,
  minDurationMs,
  hint,
};
